import React, { useState } from "react"
import {
   Modal,
   ModalHeader,
   ModalBody,
   Nav,
   NavItem,
   NavLink,
   TabContent,
   TabPane,
   Button,
} from "reactstrap"
import classnames from "classnames"
import { AddTimeLineNote, UpdateTimeLineNote } from "api/api.service"
import ViewNoteCard from "./ViewNoteCard"
import EditNoteForm from "./EditNoteForm"
import NewNoteForm from "./NewNoteForm"
import { useProductionScheduleContext } from "../ProductionScheduleContext"

import "./style.scss"

export const NOTE_TYPES = {
   PENDING: "pending",
   RESOLVED: "resolved",
}

const InspectionNoteModal = ({
   isOpen,
   toggle,
   slot,
   setSelectedSlot = () => {},
}) => {
   const [activeTab, setActiveTab] = useState("1")
   const [editingNoteId, setEditingNoteId] = useState(null)
   const [showNewForm, setShowNewForm] = useState(false)
   const [isLoading, setIsLoading] = useState(false)

   const {
      timeLineHashMap,
      changeTimeLineData = () => {},
      stageHashMap,
   } = useProductionScheduleContext()

   const timelineOrderId = slot?.timelineOrderId || slot?.timelineOrder?.id
   const stageId = slot?.stageId

   const notes = slot?.timelineOrder?.notes || []
   const pendingNotes = notes.filter(n => !n.resolved)
   const resolvedNotes = notes.filter(n => n.resolved)

   const toggleTab = tab => {
      if (activeTab !== tab) {
         setActiveTab(tab)
         setEditingNoteId(null)
         setShowNewForm(false)
      }
   }

   const handleSaveNote = async (id, data) => {
      try {
         setIsLoading(true)

         const updatedMap = new Map(timeLineHashMap)
         const res = await UpdateTimeLineNote(id, data)

         for (const [stageKey, slots] of updatedMap.entries()) {
            for (let currentSlot of slots) {
               if (
                  currentSlot.slotId === slot.slotId
                  // && stageHashMap.get(slot.stageId) >= stageHashMap.get(stageId)
               ) {
                  currentSlot.timelineOrder = {
                     ...currentSlot.timelineOrder,
                     notes: currentSlot.timelineOrder.notes.map(existingNote =>
                        existingNote.id === id
                           ? { ...existingNote, ...res }
                           : existingNote
                     ),
                  }
               }
            }
         }

         changeTimeLineData(updatedMap)
         setSelectedSlot(prev => ({
            ...prev,
            timelineOrder: {
               ...prev.timelineOrder,
               notes: prev.timelineOrder.notes.map(existingNote =>
                  existingNote.id === id
                     ? { ...existingNote, ...data }
                     : existingNote
               ),
            },
         }))
         setEditingNoteId(null)
      } catch (err) {
         console.error("Error in handleSaveNote", err)
      } finally {
         setIsLoading(false)
      }
   }

   const handleAddNote = async values => {
      try {
         setIsLoading(true)

         const payload = {
            note: values.textAreaValue,
            timelineOrderId,
            stageId,
         }

         const res = await AddTimeLineNote(payload)

         if (!res?.id) return

         const newNote = { ...res }

         const updatedMap = new Map(timeLineHashMap)

         for (const [stageKey, slots] of updatedMap.entries()) {
            for (let currentSlot of slots) {
               if (
                  currentSlot.slotId === slot.slotId &&
                  stageHashMap.get(currentSlot.stageId) >=
                     stageHashMap.get(stageId)
               ) {
                  currentSlot.timelineOrder = {
                     ...currentSlot.timelineOrder,
                     notes: [
                        ...(currentSlot.timelineOrder.notes || []),
                        newNote,
                     ],
                  }
               }
            }
         }

         changeTimeLineData(updatedMap)
         setSelectedSlot(prev => ({
            ...prev,
            timelineOrder: {
               ...prev.timelineOrder,
               notes: [...prev?.timelineOrder?.notes, newNote],
            },
         }))
         setShowNewForm(false)
         return res
      } catch (err) {
         console.error("Error in handleAddNote", err)
      } finally {
         setIsLoading(false)
      }
   }

   return (
      <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
         <ModalHeader toggle={toggle}>Inspection Notes</ModalHeader>
         <ModalBody>
            <Nav tabs>
               <NavItem>
                  <NavLink
                     className={classnames({ active: activeTab === "1" })}
                     onClick={() => toggleTab("1")}
                  >
                     Pending
                  </NavLink>
               </NavItem>
               <NavItem>
                  <NavLink
                     className={classnames({ active: activeTab === "2" })}
                     onClick={() => toggleTab("2")}
                  >
                     Resolved
                  </NavLink>
               </NavItem>
            </Nav>

            <TabContent
               activeTab={activeTab}
               className={`mt-3 modal-tab-section  ${
                  activeTab == 2 ? "modal-tab-section-scroll" : ""
               }`}
            >
               <TabPane tabId="1" className="tab-pane">
                  <div
                     className={`notes-container ${
                        showNewForm
                           ? "notes-container-form"
                           : "notes-container-button"
                     }`}
                  >
                     {pendingNotes.length === 0 && (
                        <div className="d-flex h-100 justify-content-center align-items-center">
                           <p>No pending notes.</p>
                        </div>
                     )}
                     {pendingNotes.map((note, index) =>
                        editingNoteId === note.id ? (
                           <EditNoteForm
                              key={note.id}
                              note={note}
                              onSave={handleSaveNote}
                              onCancel={() => setEditingNoteId(null)}
                              isLoading={isLoading}
                           />
                        ) : (
                           <ViewNoteCard
                              index={index}
                              key={note.id}
                              note={note}
                              type={NOTE_TYPES.PENDING}
                              onEdit={id => {
                                 setEditingNoteId(id)
                                 setShowNewForm(false)
                              }}
                              isLoading={isLoading}
                           />
                        )
                     )}
                  </div>
                  <div
                     className={`bottom-container ${
                        showNewForm
                           ? "bottom-container-form"
                           : "bottom-container-button"
                     }`}
                  >
                     {!showNewForm ? (
                        <div className="d-flex justify-content-end align-items-end">
                           <Button
                              color="primary"
                              onClick={() => {
                                 setShowNewForm(true)
                                 setEditingNoteId(null)
                              }}
                           >
                              Add New Note
                           </Button>
                        </div>
                     ) : (
                        <NewNoteForm
                           onSubmit={handleAddNote}
                           timelineOrderId={timelineOrderId}
                           stageId={stageId}
                           onCancel={() => setShowNewForm(false)}
                           isLoading={isLoading}
                        />
                     )}
                  </div>
               </TabPane>
               <TabPane tabId="2">
                  {resolvedNotes.length === 0 && (
                     <div className="d-flex h-100 justify-content-center align-items-center">
                        <p>No resolved notes.</p>
                     </div>
                  )}
                  {resolvedNotes.map((note, index) => (
                     <ViewNoteCard
                        type={NOTE_TYPES.RESOLVED}
                        index={index}
                        key={note.id}
                        note={note}
                     />
                  ))}
               </TabPane>
            </TabContent>
         </ModalBody>
      </Modal>
   )
}

export default InspectionNoteModal
